/// <reference lib="dom" />
/// <reference lib="dom.iterable" />
// Public-site bundle. GSAP for the two authored moments (hero intro, scroll-scrubbed depletion),
// Lenis as the only smooth-scroll engine. Every HTML state is already the final state, so with
// JavaScript off, or `prefers-reduced-motion: reduce`, the page simply stays as rendered.
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import Lenis from 'lenis';

gsap.registerPlugin(ScrollTrigger);

const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
const page = document.body.dataset.page ?? '';

function smoothScroll(): void {
  const lenis = new Lenis({ lerp: 0.12 });
  lenis.on('scroll', ScrollTrigger.update);
  gsap.ticker.add((t) => lenis.raf(t * 1000));
  gsap.ticker.lagSmoothing(0);
  window.addEventListener('pagehide', () => lenis.destroy(), { once: true });
}

/** Wrap each word of the headline for the stagger; the unsplit text stays the accessible name. */
function splitWords(el: HTMLElement): HTMLElement[] {
  const text = el.textContent ?? '';
  el.setAttribute('aria-label', text);
  el.textContent = '';
  return text.split(/\s+/).filter(Boolean).map((word, i, all) => {
    const span = document.createElement('span');
    span.className = 'w';
    span.textContent = word;
    span.setAttribute('aria-hidden', 'true');
    el.append(span, i < all.length - 1 ? ' ' : '');
    return span;
  });
}

function heroIntro(): void {
  const h1 = document.querySelector<HTMLElement>('[data-words]');
  const line = document.querySelector<HTMLElement>('[data-ledger]');
  if (!h1 || !line) return;
  const words = splitWords(h1);
  const qty = line.querySelector<HTMLElement>('.ledger-qty')!;
  const arrow = line.querySelector<HTMLElement>('.ledger-arrow')!;
  const out = line.querySelector<HTMLElement>('.ledger-out')!;
  const outText = out.textContent ?? '';
  const qtyText = qty.textContent ?? '';

  const type = (el: HTMLElement, text: string, seconds: number) => {
    const state = { n: 0 };
    return gsap.to(state, {
      n: text.length, duration: seconds, ease: 'none',
      onStart: () => el.classList.add('caret'),
      onUpdate: () => { el.textContent = text.slice(0, Math.round(state.n)); },
      onComplete: () => el.classList.remove('caret'),
    });
  };

  gsap.set([arrow, out], { autoAlpha: 0 });
  qty.textContent = '';
  gsap.timeline({ defaults: { ease: 'power3.out' } })
    .from(words, { yPercent: 60, autoAlpha: 0, duration: 0.7, stagger: 0.04 })
    .from('.hero-lead, .hero-cta', { y: 16, autoAlpha: 0, duration: 0.6, stagger: 0.1 }, '-=0.3')
    .add(type(qty, qtyText, 0.9), '-=0.2')
    .to(arrow, { autoAlpha: 1, duration: 0.2 })
    .set(out, { autoAlpha: 1 })
    .add(type(out, outText, 1.6));
}

/** Bars sit at their "after" width in HTML; scrolling scrubs from "before" to "after". */
function depletionScrub(): void {
  const fig = document.querySelector<HTMLElement>('[data-deplete]');
  if (!fig) return;
  const tl = gsap.timeline({ scrollTrigger: { trigger: fig, start: 'top 80%', end: 'center 45%', scrub: 0.4 } });
  fig.querySelectorAll<HTMLElement>('.bar').forEach((bar, i) => {
    const fill = bar.querySelector<HTMLElement>('.bar-fill')!;
    const num = bar.querySelector<HTMLElement>('[data-num]')!;
    const from = Number(bar.dataset.from), to = Number(bar.dataset.to), unit = bar.dataset.unit ?? '';
    const state = { v: from };
    tl.fromTo(fill, { scaleX: Number(bar.dataset.barFrom) }, { scaleX: Number(bar.dataset.barTo), ease: 'none', duration: 1 }, i * 0.15)
      .to(state, { v: to, ease: 'none', duration: 1, onUpdate: () => { num.textContent = `${Math.round(state.v)}${unit}`; } }, '<');
  });
}

/** Sticky header: surface after 24px of scroll; mobile menu with Escape, focus trap, outside click. */
function header(): void {
  const top = document.querySelector<HTMLElement>('[data-header]');
  if (!top) return;
  const onScroll = () => top.classList.toggle('is-scrolled', window.scrollY > 24);
  window.addEventListener('scroll', onScroll, { passive: true });
  onScroll();

  const toggle = top.querySelector<HTMLButtonElement>('[data-toggle]');
  const links = top.querySelector<HTMLElement>('[data-links]');
  if (!toggle || !links) return;
  // DOM order: links, CTA, toggle — the trap cycles first link -> ... -> toggle -> first link.
  const focusables = () => [...links.querySelectorAll<HTMLElement>('a'), ...top.querySelectorAll<HTMLElement>('.top-nav > a, [data-toggle]')];
  const setOpen = (open: boolean, refocus = false) => {
    links.classList.toggle('is-open', open);
    toggle.setAttribute('aria-expanded', String(open));
    if (open) links.querySelector<HTMLElement>('a')?.focus();
    else if (refocus) toggle.focus();
  };
  const isOpen = () => toggle.getAttribute('aria-expanded') === 'true';
  toggle.addEventListener('click', () => setOpen(!isOpen()));
  links.addEventListener('click', (e) => { if ((e.target as HTMLElement).closest('a')) setOpen(false); });
  document.addEventListener('click', (e) => { if (isOpen() && !top.contains(e.target as Node)) setOpen(false); });
  document.addEventListener('keydown', (e) => {
    if (!isOpen()) return;
    if (e.key === 'Escape') { e.preventDefault(); setOpen(false, true); return; }
    if (e.key !== 'Tab') return;
    const f = focusables(), first = f[0]!, last = f[f.length - 1]!;
    if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
    else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
  });
  window.matchMedia('(min-width: 721px)').addEventListener('change', (m) => { if (m.matches) setOpen(false); });
}

function heroVideo(): void {
  const v = document.querySelector<HTMLVideoElement>('video.hero-media');
  if (!v) return;
  if (reduce) { v.removeAttribute('autoplay'); v.pause(); return; }
  document.addEventListener('visibilitychange', () => { if (document.hidden) v.pause(); else void v.play().catch(() => {}); });
}

function contactForm(): void {
  const form = document.querySelector<HTMLFormElement>('[data-contact]');
  if (!form) return;
  const status = form.querySelector<HTMLElement>('[data-status]')!;
  const button = form.querySelector<HTMLButtonElement>('button[type=submit]')!;
  form.addEventListener('submit', async (e) => {
    e.preventDefault();
    if (!form.reportValidity()) return;
    button.disabled = true;
    status.className = 'form-status';
    status.textContent = 'Sending…';
    try {
      const res = await fetch('/site/contact', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(Object.fromEntries(new FormData(form))),
      });
      const body = (await res.json().catch(() => ({}))) as { error?: { message?: string } };
      if (!res.ok) throw new Error(body.error?.message ?? 'We could not send that. Email us directly.');
      form.reset();
      status.classList.add('is-ok');
      status.textContent = 'Sent. We reply within one business day.';
    } catch (err) {
      status.classList.add('is-err');
      status.textContent = err instanceof Error ? err.message : 'We could not send that. Email us directly.';
    } finally {
      button.disabled = false;
    }
  });
}

header();
heroVideo();
contactForm();
if (!reduce) {
  smoothScroll();
  if (page === 'home') {
    heroIntro();
    depletionScrub();
    void document.fonts?.ready.then(() => ScrollTrigger.refresh());
  }
}
